import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Collapse,
  Modal,
  ModalBody,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import classNames from "classnames";
import ButtonIcon from "../common/ButtonIcon";
import { Controller, useForm, useWatch } from "react-hook-form";
import NumberFormat from "react-number-format";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams, useLocation } from "react-router";
import { PreloadState } from "../UI/preloading/PreloadState";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Datetime from "react-datetime";
import { fetchProviders, createProvider } from "../../helpers/api/provider";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import moment from "moment";
import CreateProviderForm from "../providers/CreateProviderForm";
import { Toast } from "../UI/toast/Toast";
import ExpensePaymentFields, { ExpensePaidNotice, emptyExpensePayment, expensePaymentError, expensePaymentPayload } from "./ExpensePaymentFields";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { fetchAccountingAreas, createAccountingArea } from "../../helpers/api/accountingAreas";
import { fetchAccountPlans } from "../../helpers/api/accountPlans";
import { findAllCurrencies, findAllExchangeRates } from "../../helpers/api/currency";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import FullPagination from "../common/pagination/FullPagination";
import SelectProviderListItem from "../common/SelectProviderListItem";
import { faUndo, faSearch, faSave, faCog, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CurrencyContext } from "../../context/Context";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import {
  convertToUsd,
  convertFromUsd,
  formatAmountForBackend,
  formatAmountRounded,
  getCurrencyName,
} from "../../helpers/currencyConversion.js";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import { fetchAllPages } from "../../helpers/api/pagination";

const CreateCommonCostsEditForm = ({
  commonCost,
  handleSubmitData,
  setRedirectUrl,
  common,
}) => {
  const moduleBase = useModuleBase();

  const { id } = useParams();
  const history = useHistory();
  const { selectedCondominium } = useSelector((stateRedux) => stateRedux.condominium);
  const { role } = useSelector((stateRedux) => stateRedux.auth.user);
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const condominiumId = id || selectedCondominium;
  const activeExchangeRate = Number(curr?.value ?? 0);
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    control,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      name: common?.name || "",
      description: common?.description || "",
      value: common?.value || "",
      montoOriginal: common?.value ?? "",
      monedaOriginal: baseCurrency.iso,
      accounting_area_id: common?.sub_expenses?.[0]?.accounting_area?.id || "",
      expired_at: common?.expired_at || moment().format("YYYY-MM-DD HH:mm:ss"),
      type_payment_supplier: common?.type_payment_supplier || "",
      is_provider_common: common?.provider?.is_common ?? 1,
      provider_name: common?.provider_name || "",
      provider_email: common?.provider_email || "",
      provider_dni: common?.provider_dni || "",
      provider_phone: common?.provider_phone || "",
      provider_address: common?.provider_address || "",
      provider_description: common?.provider_description || "",
    },
  });

  const watchedOriginalAmount = useWatch({
    control,
    name: "montoOriginal",
  });

  const watchedOriginalCurrency = useWatch({
    control,
    name: "monedaOriginal",
    defaultValue: "",
  });

  const watchedExpenseDate = useWatch({
    control,
    name: "expired_at",
    defaultValue: common?.expired_at || "",
  });

  const [providerSearch, setProviderSearch] = useState("");
  const [params, setParams] = useState({
    condominiumId: id,
    search: null,
  });

  const applyProviderSearch = (value = providerSearch) => {
    const term = typeof value === "string" ? value.trim() : "";
    setProviderSearch(term);
    setParams((prev) => ({ ...prev, search: term || null }));
  };

  const {
    mutate,
    data,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchProviders, params);

  const isProviderFrequent = common?.provider?.is_common === 1;

  const [selectedProvider, setSelectedProvider] = useState(
    isProviderFrequent ? common?.provider?.id : null
  );
  const [selectedProviderNotFrequent, setSelectedProviderNotFrequent] =
    useState(common ? !isProviderFrequent : false);

  const [createProviderModalOpen, setCreateProviderModalOpen] = useState(false);
  const [nofrecuente, noFrecuenteOpen] = useState(
    common ? !isProviderFrequent : false
  );

  const [selectedDate, setSelectedDate] = useState(
    common?.expired_at ? moment(common.expired_at) : moment()
  );

  const [accountingAreas, setAccountingAreas] = useState([]);
  const [accountPlans, setAccountPlans] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [hasInitializedUsdDefaults, setHasInitializedUsdDefaults] = useState(false);

  // El endpoint trae TODAS las áreas contables del condominio (de gastos y de
  // ingresos mezcladas); acá solo dejamos pasar las que apuntan a una cuenta
  // de Gastos (grupo "6"), para no mostrar áreas creadas desde el flujo de
  // ingresos.
  const expenseAccountingAreas = accountingAreas.filter((area) => {
    const code = area.account_plan?.code || area.accountPlan?.code;
    return code && code.startsWith("6");
  });
  const [createAccountAreaModalOpen, setCreateAccountAreaModalOpen] = useState(false);
  const [payment, setPayment] = useState(emptyExpensePayment);
  const amountLocked = Boolean(common?.next_charge?.amount_locked);
  const [newAreaName, setNewAreaName] = useState("");
  const [selectedAccountPlanId, setSelectedAccountPlanId] = useState("");
  const [isCreatingArea, setIsCreatingArea] = useState(false);
  const [pendingAreaId, setPendingAreaId] = useState(null);
  const { state } = useLocation();

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const bolivaresCurrency = currencyCatalog.find((item) => {
      const name = (item?.name || "").toLowerCase();
      const iso = (item?.iso || "").toLowerCase();
      const symbol = (item?.symbol || "").toLowerCase();

      return name.includes("boliva") || iso === "ves" || symbol === "bs" || iso === "bs";
    });

    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return bolivaresCurrency || fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(watchedOriginalCurrency || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, watchedOriginalCurrency]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  const parsedExpenseDate = useMemo(() => {
    if (!watchedExpenseDate) {
      return null;
    }

    const parsedDate = moment(watchedExpenseDate);
    return parsedDate.isValid() ? parsedDate : null;
  }, [watchedExpenseDate]);

  // DEBUG: Efecto para verificar el estado de selección
  useEffect(() => {
    console.log("=== DEBUG SELECTION ===");
    console.log("selectedProvider:", selectedProvider);
    console.log("common?.provider?.id:", common?.provider?.id);
    console.log("isProviderFrequent:", isProviderFrequent);
    if (data && selectedProvider) {
      const foundProvider = data.find((p) => p.id === selectedProvider);
      console.log("Found in data:", foundProvider);
    }
  }, [selectedProvider, data, common, isProviderFrequent]);

  const getAccountingData = async () => {
    try {
      const [areasRes, plansRes] = await Promise.all([
        fetchAccountingAreas(id),
        fetchAccountPlans(id)
      ]);
      setAccountingAreas(areasRes.data || []);
      setAccountPlans(plansRes.data || []);
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleDateChange = (date) => {
    try {
      const formattedDate = moment(date).isValid()
        ? moment(date).format("YYYY-MM-DD HH:mm:ss")
        : moment().format("YYYY-MM-DD HH:mm:ss");
      setSelectedDate(date);
      setValue("expired_at", formattedDate);
    } catch (error) {
      const defaultDate = moment().format("YYYY-MM-DD HH:mm:ss");
      setSelectedDate(moment());
      setValue("expired_at", defaultDate);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const [areasRes, plansRes] = await Promise.all([
          fetchAccountingAreas(id),
          fetchAccountPlans(id)
        ]);
        if (isMounted) {
          setAccountingAreas(areasRes.data || []);
          setAccountPlans(plansRes.data || []);
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!condominiumId) return;

      setHasLoadedCurrencies(false);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: condominiumId,
        }));

        if (isMounted) {
          setAvailableCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setHasLoadedCurrencies(true);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [condominiumId]);

  useEffect(() => {
    if (!currencyCatalog.length) {
      return;
    }

    if (watchedOriginalCurrency && selectedCurrencyData) {
      return;
    }

    const currentCurrencyId = curr?.currency?.id;
    const fallbackByCurrent = currencyCatalog.find(
      (item) => String(item.id) === String(currentCurrencyId)
    );
    const fallbackByUsd = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );
    const fallback = fallbackByCurrent || fallbackByUsd || currencyCatalog[0];

    if (fallback) {
      setValue("monedaOriginal", String(fallback.id), {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [currencyCatalog, curr, selectedCurrencyData, setValue, watchedOriginalCurrency]);

  const {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedExpenseDate,
    activeExchangeRate,
    condominiumId: condominiumId || selectedCondominium,
  });

  const isSelectedDateToday =
    Boolean(parsedExpenseDate) && parsedExpenseDate.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");

  const handleCreateAccountingArea = async () => {
    // Sin este guard, varios clicks rápidos disparan varios POST antes de que
    // el primero responda, creando la misma área duplicada.
    if (!newAreaName || isCreatingArea) return;
    setIsCreatingArea(true);
    try {
      // Intentar encontrar el plan de Gastos Operativos (6.1) por defecto si no hay uno seleccionado
      let planId = selectedAccountPlanId;
      if (!planId) {
        const defaultPlan = accountPlans.find(p => p.code === '6.1');
        planId = defaultPlan ? defaultPlan.id : null;
      }

      const response = await createAccountingArea({
        condominium_id: id,
        name: newAreaName,
        account_plan_id: planId,
      });
      if (response.code === SUCCESS_RESPONSE.OK || response.code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title: "Área creada con éxito",
        });
        setNewAreaName("");
        setSelectedAccountPlanId("");
        setCreateAccountAreaModalOpen(false);
        setPendingAreaId(response.data?.id ?? null);
        await getAccountingData();
      }
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsCreatingArea(false);
    }
  };

  useEffect(() => {
    if (selectedProvider) {
      setValue("provider_id", selectedProvider);
      clearErrors("provider_id");
    }
    if (selectedProviderNotFrequent) {
      clearErrors("provider_id");
    }
  }, [selectedProvider, selectedProviderNotFrequent, setValue, clearErrors]);

  useEffect(() => {
    if (common && accountingAreas.length > 0) {
      const areaId = common.sub_expenses?.[0]?.accounting_area?.id || "";
      console.log("Setting accounting_area_id to:", areaId);
      setValue("accounting_area_id", areaId);
    }
  }, [common, accountingAreas, setValue]);

  useEffect(() => {
    if (pendingAreaId && expenseAccountingAreas.some((area) => area.id === pendingAreaId)) {
      setValue("accounting_area_id", pendingAreaId, { shouldValidate: true });
      setPendingAreaId(null);
    }
  }, [accountingAreas, pendingAreaId, setValue]);

  useEffect(() => {
    if (common) {
      reset({
        ...common,
        montoOriginal: common?.value ?? "",
        monedaOriginal: baseCurrency.iso,
        accounting_area_id: common.sub_expenses?.[0]?.accounting_area?.id || "",
        is_provider_common: common?.provider?.is_common ?? 1,
        expired_at: common?.expired_at || moment().format("YYYY-MM-DD HH:mm:ss"),
      });
      setSelectedDate(common?.expired_at ? moment(common.expired_at) : moment());
      setHasInitializedUsdDefaults(false);
    }
  }, [baseCurrency.iso, common, reset]);

  useEffect(() => {
    if (!common || !currencyCatalog.length || hasInitializedUsdDefaults) {
      return;
    }

    const usdCurrency = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    if (!usdCurrency) {
      return;
    }

    setValue("monedaOriginal", String(usdCurrency.id), {
      shouldDirty: false,
      shouldValidate: true,
    });

    setValue("montoOriginal", common?.value ?? "", {
      shouldDirty: false,
      shouldValidate: true,
    });

    setHasInitializedUsdDefaults(true);
  }, [
    baseCurrency.iso,
    common,
    currencyCatalog,
    hasInitializedUsdDefaults,
    setValue,
  ]);

  useEffect(() => {
    if (!common || !currencyCatalog.length || selectedCurrencyData) {
      return;
    }

    const currentMoneda = String(watch("monedaOriginal") || "");
    if (!currentMoneda) {
      return;
    }

    const mappedByIso = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === currentMoneda.toUpperCase()
    );

    if (mappedByIso) {
      setValue("monedaOriginal", String(mappedByIso.id), {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [common, currencyCatalog, selectedCurrencyData, setValue, watch]);

  useEffect(() => {
    if (!hasLoadedCurrencies || !preferredDefaultCurrency || !common) {
      return;
    }

    const preferredCurrencyId = String(preferredDefaultCurrency.id);
    const currentCurrencyId = String(watchedOriginalCurrency || "");

    if (currentCurrencyId !== preferredCurrencyId) {
      setValue("monedaOriginal", preferredCurrencyId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [common, hasLoadedCurrencies, preferredDefaultCurrency, setValue, watchedOriginalCurrency]);

  useEffect(() => {
    if (!common || !hasLoadedCurrencies || !preferredDefaultCurrency) {
      return;
    }

    if (!selectedCurrencyData) {
      return;
    }

    const preferredCurrencyId = String(preferredDefaultCurrency.id);
    const currentCurrencyId = String(selectedCurrencyData.id || "");

    if (currentCurrencyId !== preferredCurrencyId) {
      return;
    }

    if (!common?.value) {
      return;
    }

    if ((preferredDefaultCurrency?.iso || "").toUpperCase() === baseCurrency.iso) {
      setValue("montoOriginal", common.value, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    if (!appliedExchangeRate || appliedExchangeRate <= 0) {
      return;
    }

    if (!isSelectedCurrencyUsd && parsedExpenseDate && !rateResolution?.selectedDateKey) {
      return;
    }

    setValue(
      "montoOriginal",
      String(convertFromUsd(common.value, false, appliedExchangeRate)),
      {
        shouldDirty: false,
        shouldValidate: true,
      }
    );
  }, [
    appliedExchangeRate,
    baseCurrency.iso,
    common,
    hasLoadedCurrencies,
    isSelectedCurrencyUsd,
    parsedExpenseDate,
    preferredDefaultCurrency,
    rateResolution,
    selectedCurrencyData,
    setValue,
  ]);

  useEffect(() => {
    setRedirectUrl(`/dashboard/providers`);
  }, [setRedirectUrl]);

  useEffect(() => {
    [
      "is_provider_common",
      "provider_name",
      "provider_email",
      "provider_dni",
      "provider_phone",
      "provider_address",
      "provider_description",
    ].forEach((field) => register(field));
  }, [register]);

  // Efecto para inicializar según el tipo de proveedor
  useEffect(() => {
    if (common) {
      if (isProviderFrequent) {
        // Proveedor frecuente
        setSelectedProvider(common.provider?.id || null);
        setValue("is_provider_common", 1);
        noFrecuenteOpen(false);
        setSelectedProviderNotFrequent(false);
        console.log("Proveedor frecuente inicializado:", common.provider?.id);
      } else {
        // Proveedor no frecuente
        noFrecuenteOpen(true);
        setSelectedProviderNotFrequent(true);
        setValue("is_provider_common", 0);
        setValue("provider_name", common.provider_name || "");
        setValue("provider_email", common.provider_email || "");
        setValue("provider_dni", common.provider_dni || "");
        setValue("provider_phone", common.provider_phone || "");
        setValue("provider_address", common.provider_address || "");
        setValue("provider_description", common.provider_description || "");
      }
    }
  }, [common, isProviderFrequent]);

  const renderProvidersItems = () => {
    if (!data || !Array.isArray(data)) {
      return null;
    }

    return data.map((provider) => {
      const isSelected = selectedProvider === provider.id;
      console.log(
        `Proveedor ${provider.id} - ${provider.name}: seleccionado=${isSelected}`
      );
      return (
        <SelectProviderListItem
          avatar={"https://picsum.photos/200/200"}
          onSelect={() => {
            setSelectedProvider(provider.id);
            setSelectedProviderNotFrequent(false);
            noFrecuenteOpen(false);
            setValue("is_provider_common", 1);
          }}
          selected={selectedProvider}
          id={provider.id}
          key={provider.id}
          title={provider.name || "Proveedor sin nombre"}
          toDeleteId={provider.id}
          toEditId={provider.id}
          createdAt={provider.created_at}
          alternativeName="proveedor"
        />
      );
    });
  };

  useEffect(() => {
    if (!state?.type_id || !state?.type_name) {
      history.push(`/dashboard/finance`);
    }
  }, [state]);

  const handleCreateProviderSubmit = async (values) => {
    try {
      const response = await createProvider(id, values);
      const isSuccess =
        response?.code === SUCCESS_RESPONSE.CREATED ||
        response?.code === SUCCESS_RESPONSE.OK ||
        response?.data?.id;

      if (isSuccess) {
        setCreateProviderModalOpen(false);
        Toast.fire({
          type: "success",
          title: "Se ha creado el proveedor satisfactoriamente.",
        });
        const createdId = response?.data?.id;
        if (createdId) {
          setSelectedProvider(createdId);
          noFrecuenteOpen(false);
          setValue("is_provider_common", 1);
          applyProviderSearch(response.data.name);
        }
        mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  useEffect(() => {
    const convertedValue = convertToUsd(
      watchedOriginalAmount,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    setValue("value", convertedValue === "" ? "" : String(convertedValue));
  }, [appliedExchangeRate, isSelectedCurrencyUsd, setValue, watchedOriginalAmount]);

  const resolveRateForCurrency = async (currencyData) => {
    if (!currencyData) {
      return null;
    }

    const isUsdCurrency =
      (currencyData?.iso || "").toUpperCase() === baseCurrency.iso ||
      currencyData?.isFallbackUsd;

    if (isUsdCurrency) {
      return 1;
    }

    try {
      const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
        currency: currencyData.id,
      }));

      const rates = response?.data || [];
      const selectedDateKey = parsedExpenseDate?.format("YYYY-MM-DD");
      const rateForDay = selectedDateKey
        ? rates
          .filter(
            (rate) =>
              moment(rate.date || rate.created_at).format("YYYY-MM-DD") ===
              selectedDateKey
          )
          .sort(
            (a, b) =>
              moment(b.date || b.created_at).valueOf() -
              moment(a.date || a.created_at).valueOf()
          )[0]
        : null;

      if (selectedDateKey && !rateForDay) {
        return 0;
      }

      return Number(rateForDay?.value || appliedExchangeRate || activeExchangeRate || 0);
    } catch (error) {
      return Number(appliedExchangeRate || activeExchangeRate || 0);
    }
  };

  const handleCurrencyChange = async (event) => {
    const nextCurrency = event.target.value;
    const nextCurrencyData = (currencyCatalog || []).find(
      (item) => String(item.id) === String(nextCurrency)
    );
    const currentCurrencyData = selectedCurrencyData;

    if (!nextCurrencyData || !currentCurrencyData) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const currentIsUsd =
      (currentCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;
    const nextIsUsd =
      (nextCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

    if (!currentIsUsd && !nextIsUsd) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    let nextOriginalAmount = watchedOriginalAmount;

    if (currentIsUsd && !nextIsUsd) {
      const targetRate = await resolveRateForCurrency(nextCurrencyData);

      if (!targetRate || targetRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertFromUsd(
        watchedOriginalAmount,
        false,
        targetRate
      );
    } else if (!currentIsUsd && nextIsUsd) {
      const sourceRate =
        Number(appliedExchangeRate || 0) > 0 && !rateResolution?.isFallback
          ? Number(appliedExchangeRate)
          : await resolveRateForCurrency(currentCurrencyData);

      if (!sourceRate || sourceRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertToUsd(
        watchedOriginalAmount,
        false,
        sourceRate
      );
    }

    setValue("monedaOriginal", nextCurrency, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(
      "montoOriginal",
      nextOriginalAmount === "" ? "" : String(nextOriginalAmount),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const handleFormSubmit = async (values) => {

    if (!values.expired_at) {
      Toast.fire({
        type: "error",
        title: "La fecha del gasto es obligatoria.",
      });
      return;
    }

    if (!isSelectedCurrencyUsd && (!appliedExchangeRate || appliedExchangeRate <= 0 || rateResolution?.isFallback)) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
      });
      return;
    }

    const normalizedUsdValue = convertToUsd(
      values.montoOriginal,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    const isCondominiumExpense = Number(state?.type_id) === 4;
    const paymentAmount = isCondominiumExpense ? common?.next_charge?.pending : normalizedUsdValue;
    const paymentError = expensePaymentError(payment, paymentAmount);
    if (paymentError) {
      Toast.fire({ type: "error", title: paymentError });
      return;
    }

    await handleSubmitData({
      ...values,
      ...expensePaymentPayload(payment, paymentAmount),
      value: amountLocked
        ? formatAmountForBackend(common?.value)
        : normalizedUsdValue === "" ? "" : formatAmountForBackend(normalizedUsdValue),
    });
  };

  const usdEquivalent = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return null;
    }

    const convertedAmount = convertToUsd(
      watchedOriginalAmount,
      false,
      appliedExchangeRate
    );

    return convertedAmount === "" ? null : convertedAmount;
  }, [appliedExchangeRate, isSelectedCurrencyUsd, watchedOriginalAmount]);

  const helpText = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return "";
    }

    if (!parsedExpenseDate || !parsedExpenseDate.isValid()) {
      return "Selecciona la fecha primero";
    }

    const dateLabel = parsedExpenseDate.format("DD/MM/YYYY");

    if (isRateLoading) {
      return "";
    }

    if (usdEquivalent === null) {
      return "";
    }

    const rateLabel = appliedExchangeRate
      ? formatAmountRounded(appliedExchangeRate, 2)
      : "N/D";

    return `Tasa ${dateLabel}: ${rateLabel} | Equivalente aproximado: ${baseCurrency.symbol}${formatAmountRounded(usdEquivalent, 2)} ${baseCurrency.iso}`;
  }, [
    appliedExchangeRate,
    isRateLoading,
    isSelectedCurrencyUsd,
    parsedExpenseDate,
    rateResolution,
    usdEquivalent,
    baseCurrency,
  ]);

  const fallbackRateWarningText = useMemo(() => {
    if (isSelectedCurrencyUsd || !rateResolution?.isFallback || !rateResolution?.selectedDateKey) {
      return "";
    }

    const selectedDateLabel = moment(rateResolution.selectedDateKey).format("DD/MM/YYYY");
    return role === "manager"
      ? `No hay tasa registrada para ${selectedDateLabel}. Debes registrarla para poder guardar.`
      : `No hay tasa registrada para ${selectedDateLabel}. Comunícate con el administrador para que la registre.`;
  }, [isSelectedCurrencyUsd, rateResolution, role]);

  const renderForm = () => {
    return (
      <>
        <FalconCardHeader title="Detalles del gasto" light={true} />
        <CardBody>
          <FormGroup>
            <Label for="name">Nombre</Label>
            <Input
              autoComplete="off"
              id="name"
              name="name"
              onChange={(event) => {
                setValue("name", event.target.value);
              }}
              disabled={false}
              value={watch("name") || ""}
              innerRef={register("name", {
                required: "Nombre del gasto es requerido.",
                minLength: {
                  value: 4,
                  message:
                    "El nombre del gasto debe tener al menos 4 caracteres.",
                },
              })}
              placeholder="Nombre del gasto"
              className={classNames({
                "border-danger": errors.name?.message,
              })}
            />
            {errors.name && (
              <WizardError
                error={errors.name.message}
                className="fs--1 font-weight-normal d-block"
              />
            )}
          </FormGroup>

          <Row form>
            <Col md={6}>
              <FormGroup>
                <Label for="accounting_area_id">Cuenta / Área Contable</Label>
                <Input
                  type="select"
                  name="accounting_area_id"
                  className={classNames({
                    "border-danger": errors.accounting_area_id,
                  })}
                  innerRef={register({
                    required: "Debes seleccionar un área contable.",
                  })}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Selecciona una Cuenta / Área
                  </option>
                  {expenseAccountingAreas.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </Input>
                {errors.accounting_area_id && (
                  <WizardError
                    error={errors.accounting_area_id.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                )}
                <Button
                  color="link"
                  size="sm"
                  className="p-0 mt-1"
                  onClick={() => setCreateAccountAreaModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faCog} className="mr-1" />
                  Nueva Área/Categoría
                </Button>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="description">Descripción <span className="text-danger">*</span></Label>
                <Input
                  autoComplete="off"
                  name="description"
                  disabled={false}
                  onChange={(event) => {
                    setValue("description", event.target.value);
                  }}
                  value={watch("description") || ""}
                  innerRef={register("description", {
                    required: "La descripción es requerida.",
                    minLength: {
                      value: 4,
                      message: "La descripción debe tener al menos 4 carácteres.",
                    },
                  })}
                  type={"textarea"}
                  rows={2}
                  className={`${errors.description?.message ? "border-danger" : ""
                    } form-control`}
                  placeholder="Descripción general del gasto"
                />
                <WizardError
                  error={errors.description?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </FormGroup>
            </Col>
          </Row>
          <Modal isOpen={createAccountAreaModalOpen} toggle={() => setCreateAccountAreaModalOpen(!createAccountAreaModalOpen)}>
            <div className="modal-header">
              <h5 className="modal-title">Crear Nueva Área / Categoría de Gasto</h5>
              <button type="button" className="close" onClick={() => setCreateAccountAreaModalOpen(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <ModalBody>
              <FormGroup>
                <Label for="newAreaName">Nombre del Área (ej: Mantenimiento Ascensor)</Label>
                <Input
                  type="text"
                  id="newAreaName"
                  value={newAreaName}
                  onChange={(e) => setNewAreaName(e.target.value)}
                  placeholder="Ingrese el nombre"
                />
              </FormGroup>
              <div className="d-flex justify-content-end">
                <Button color="secondary" className="mr-2" onClick={() => setCreateAccountAreaModalOpen(false)} disabled={isCreatingArea}>
                  Cancelar
                </Button>
                <Button color="primary" onClick={handleCreateAccountingArea} disabled={isCreatingArea}>
                  {isCreatingArea ? "Creando..." : "Crear Área"}
                </Button>
              </div>
            </ModalBody>
          </Modal>

          <Row form className="pt-3">
              <Col sm tag={FormGroup}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0">
                    Seleccione un proveedor de la lista
                  </Label>
                </div>
                <Row>
                  <Col xs={12} md={""} className="d-flex mb-2 mb-3">
                    <Input
                      type="text"
                      value={providerSearch}
                      placeholder="Buscar por nombre, cédula, teléfono, correo, descripción o dirección..."
                      className="d-inline-block search-input mr-2"
                      onKeyPress={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          applyProviderSearch(event.target.value);
                        }
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setProviderSearch(value);
                        if (!value.trim()) {
                          setParams((prev) => ({ ...prev, search: null }));
                        }
                      }}
                    />
                    <ButtonIcon
                      icon={faSearch}
                      className="d-inline-block text-nowrap mr-2"
                      color="falcon-default"
                      type="button"
                      onClick={() => applyProviderSearch()}
                    >
                      Buscar
                    </ButtonIcon>
                    <Button
                      color="primary"
                      className="d-inline-block text-nowrap"
                      type="button"
                      onClick={() => setCreateProviderModalOpen(true)}
                    >
                      Crear nuevo proveedor
                    </Button>
                  </Col>
                </Row>
                <Row className="m-0 p-0">
                  <Col sm={12} tag={FormGroup} className="m-0 p-0">
                    {renderProvidersItems()}
                    {selectedProvider &&
                      !data?.find((p) => p.id === selectedProvider) && (
                        <div className="alert alert-info mt-2">
                          El proveedor seleccionado no está en la lista actual.
                          Puede que esté en otra página o haya sido eliminado.
                        </div>
                      )}
                  </Col>
                </Row>
                <FullPagination
                  nextPage={nextPage}
                  prevPage={previousPage}
                  goToPage={goToPage}
                  meta={meta}
                  className={"shadow-none"}
                  posText={"proveedores"}
                  perPage={perPage}
                />
                <WizardError
                  error={errors.is_provider_common?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </Col>
            </Row>

          <Modal
            isOpen={createProviderModalOpen}
            toggle={() => setCreateProviderModalOpen(false)}
          >
            <ModalBody>
              <CreateProviderForm
                condominiumId={id}
                handleSubmitData={handleCreateProviderSubmit}
                setRedirect={() => { }}
                setRedirectUrl={() => { }}
                onCancel={() => setCreateProviderModalOpen(false)}
              />
            </ModalBody>
          </Modal>

          <Collapse isOpen={nofrecuente}>
            <Row>
              <Col sm={6} tag={FormGroup}>
                <Label for="value">Proveedor no frecuente: </Label>
                <h6>
                  {watch("provider_name") || "No se ha ingresado proveedor"}
                </h6>
              </Col>
            </Row>
          </Collapse>


          <Row form>
            <Col sm={6} className="w-100">
              <FormGroup>
                <Label for="expired_at">Fecha del gasto <span className="text-danger">*</span></Label>
                <Datetime
                  value={selectedDate}
                  className={`w-100 ${errors.expired_at?.message ? "border-danger" : ""
                    }`}
                  name="expired_at"
                  innerRef={register("expired_at", {
                    required: "La fecha del gasto es obligatoria.",
                  })}
                  onChange={handleDateChange}
                  dateFormat="DD/MM/YYYY"
                  timeFormat={false}
                />
                <WizardError
                  error={errors.expired_at?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </FormGroup>
            </Col>
            <Col sm={6} tag={FormGroup}>
              <FormGroup>
                <Label for="montoOriginal">Monto del gasto</Label>
                <Controller
                  name="montoOriginal"
                  control={control}
                  rules={{ required: "Monto es requerido" }}
                  render={({ onChange, value }) => (
                    <>
                      <InputWithLeftSelect
                        selectValue={watchedOriginalCurrency || ""}
                        onSelectChange={handleCurrencyChange}
                        selectOptions={(currencyCatalog || []).map((item) => ({
                          value: String(item.id),
                          label: `${item.iso} (${item.name})`,
                        }))}
                        errorMessage={errors.montoOriginal?.message}
                        helpText={helpText}
                        selectAriaLabel="Moneda original"
                        selectMinWidth="120px"
                        selectDisabled={amountLocked}
                      >
                        <NumberFormat
                          customInput={Input}
                          disabled={amountLocked}
                          thousandSeparator
                          prefix={
                            selectedCurrencyData?.iso
                              ? `${selectedCurrencyData.iso} `
                              : ""
                          }
                          fixedDecimalScale
                          decimalScale={2}
                          onValueChange={(vals) => onChange(vals.value)}
                          value={value}
                          placeholder="0.00"
                          className={classNames("font-weight-bold border-left-0", {
                            "border-danger": errors.montoOriginal,
                          })}
                        />
                      </InputWithLeftSelect>
                      {Boolean(parsedExpenseDate && parsedExpenseDate.isValid()) && isRateLoading && (
                        <small className="text-muted d-block mt-1">
                          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                          Consultando tasa para la fecha...
                        </small>
                      )}
                    </>
                  )}
                />
              </FormGroup>
            </Col>
          </Row>

          {Boolean(watchedOriginalAmount) && !amountLocked && (
            <Card className="border-warning bg-soft-warning mb-3">
              <CardBody className="py-3">
                <div className="d-flex align-items-start">
                  <div>
                    <div className="text-700 fs--1">
                      El monto que se guardará en el sistema será el equivalente en {getCurrencyName(baseCurrency.iso)} {baseCurrency.symbol}.
                      El valor mostrado arriba es solo la referencia original ingresada por el usuario.
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {!commonCost && common?.next_charge && (common.next_charge.paid || amountLocked) && (
            <ExpensePaidNotice
              title={
                Number(state?.type_id) !== 4
                  ? "El próximo cobro de este gasto ya está pagado."
                  : common.next_charge.paid
                    ? "La cuenta por pagar de este gasto ya está pagada."
                    : "La cuenta por pagar de este gasto ya tiene un pago."
              }
              detail={amountLocked ? "Para cambiar el monto, anula el pago en Pagos a proveedores." : null}
            />
          )}

          {!commonCost && common?.next_charge && !common.next_charge.paid && (
            <ExpensePaymentFields
              condominiumId={id}
              amount={
                Number(state?.type_id) === 4
                  ? common.next_charge.pending
                  : isSelectedCurrencyUsd
                    ? Number(watchedOriginalAmount || 0)
                    : usdEquivalent
              }
              value={payment}
              onChange={setPayment}
              helpText={Number(state?.type_id) === 4 ? "Si ya le pagaste al proveedor, registra el pago aquí: la cuenta por pagar de este gasto queda pagada." : "Si ya le pagaste al proveedor el próximo cobro de este gasto, registra el pago aquí: cuando se emita la planilla, su cuenta por pagar ya va a estar pagada."}
            />
          )}

          {!commonCost && (
            <Row
              form
              className="align-items-center justify-content-center justify-content-md-end"
            >
              <ButtonIcon
                color="falcon-default"
                className="my-2 mx-0 mx-md-2 m-md-0 px-5 mr-2"
                icon={faUndo}
                iconAlign="right"
                transform="down-1 shrink-4"
                onClick={() =>
                  history.push({
                    pathname: `/dashboard/${moduleBase}/${id}/common-costs`,
                    state: {
                      type_id: state?.type_id,
                      type_name: state?.type_name,
                    },
                  })
                }
              >
                Volver
              </ButtonIcon>
              <ButtonIcon
                data-testid="submit-button"
                color="primary"
                className="my-2 mx-0 mx-md-2 m-md-0 px-5"
                type="submit"
                icon={faSave}
                iconAlign="right"
                onClick={handleSubmit(handleFormSubmit)}
                disabled={isSubmitting || isRateLoading}
                transform="down-1 shrink-4"
              >
                Guardar
              </ButtonIcon>
            </Row>
          )}
        </CardBody>
      </>
    );
  };

  return (
    <Card
      className="bg-light mb-3"
      data-testid="form"
      tag={Form}
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <PreloadState loading={isSubmitting} message={`Guardando ${state?.type_name || "gasto"}...`}>
        {renderForm()}
      </PreloadState>
    </Card>
  );
};

export default withRedirect(CreateCommonCostsEditForm);
